import React, { useContext } from 'react'
import styled from 'styled-components'
import Modal, { PrevButton } from '@jarvis.oddle/core/components/Modal'
import { Text } from '@jarvis.oddle/core/components/Typo'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import modalController, {
  makeControlledModal
} from 'src/decorators/ModalController'
import { paddingSizes } from '@jarvis.oddle/core/theme/default'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import IconClose from '@jarvis.oddle/core/components/Icons/IconClose'
import Flex from '@jarvis.oddle/core/components/Flex'
import Div from '@jarvis.oddle/core/components/Div'
import IconNewStore from 'src/components/icons/IconNewStore'
import IconEmail from 'src/components/icons/IconEmail'
import IconPhone from '@jarvis.oddle/core/components/Icons/IconPhone'

const Container = styled(Modal.FlexContainer).attrs(() => ({
  width: '100%',
  height: '100%',
  flexDirection: 'column',
  padding: paddingSizes.medium
}))`
  position: relative;
  background: ${(props) => props.theme.colorContainerColor};
  width: 95%;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  transform: translateX(-50%) translateY(-50%);
  top: 50%;
  left: 50%;
  height: fit-content;
  ${breakpoint('desktop')`
    width: 100%;
    max-width: 600px;
    height: fit-content;
  `}
`

const CloseButtonContainer = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  role: 'button'
}))`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: 10;
`

const StyledPrevButton = styled(PrevButton)`
  position: absolute;
  top: 30px;
  right: 10px;
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: 10;
`

const StyledIconStore = styled(Div)`
  margin-top: 40px;
  margin-bottom: 20px;
`

const StyledContactInfo = styled(Div)`
  width: 100%;
  height: 108px;
  border-radius: 4px;
  background-color: #f1f1f1;
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin-top: 15px;
  flex-direction: column;
`

const StyledIconInfoStore = styled(Div)`
  margin-right: 5px;
  margin-top: 2px;
`
const StyledIconInfoPhone = styled(Div)`
  margin-right: 5px;
`

const StyledDescription = styled(Text)`
  margin-top: 20px;
  text-align: center;
`

const StyledLink = styled.a`
  font-weight: bold;
`

const StyledContactLink = styled.a`
  text-decoration: underline;
`

interface ContactHelpModalProps {
  modal: {
    show: boolean
    onClose: (x: () => void) => void
  }
  emailStore: string
  phoneStore: string
  addressStore: string
  isPickup: boolean
  id: string
  deliveryFulfilmentParty: {
    deliveryParty?: string
    partnerDisplayName?: string
  }
  enableChatSupportByCountry: boolean
}

const ContactRestaurantModal = ({
  modal,
  phoneStore,
  emailStore,
  addressStore,
  isPickup,
  id,
  deliveryFulfilmentParty,
  enableChatSupportByCountry
}: ContactHelpModalProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  return (
    <Modal {...modal} show={modal.show} slideInAnimation='right'>
      <Container>
        <CloseButtonContainer
          onClick={modal.onClose}
          data-testid='button.close'
        >
          <IconClose width={14} height={14} />
        </CloseButtonContainer>
        {!isPickup &&
          deliveryFulfilmentParty?.deliveryParty === 'Oddle Logistics' && (
            <StyledPrevButton
              onClick={() =>
                modal.onClose(() =>
                  modalController.showModal('NeedHelpModal', {
                    isPickup,
                    phoneStore,
                    emailStore,
                    addressStore,
                    id,
                    deliveryFulfilmentParty,
                    enableChatSupportByCountry
                  })
                )
              }
            >
              <PrevButton data-testid='backButton' />
            </StyledPrevButton>
          )}
        <Flex
          justifyContent='center'
          flexDirection='column'
          alignItems='center'
        >
          <StyledIconStore>
            <IconNewStore width={54} height={48} />
          </StyledIconStore>
          <Text>{t('We got you covered. Reach out to us below:')}</Text>
          <StyledContactInfo>
            {emailStore && (
              <Flex data-testid='reachOutText'>
                <StyledIconInfoStore>
                  <IconEmail width={18} height={13} fill={'#000'} />
                </StyledIconInfoStore>
                {
                  emailStore !== '-' ? (
                    <StyledLink href={`mailto:${emailStore}`}>
                      {emailStore}
                    </StyledLink>
                  ) : emailStore
                }
              </Flex>
            )}
            {phoneStore && (
              <Flex data-testid='reachOutText'>
                <StyledIconInfoPhone>
                  <IconPhone width={20} height={18} fill={'#000'} />
                </StyledIconInfoPhone>
                <StyledLink href={`tel:${phoneStore}`}>{phoneStore}</StyledLink>
              </Flex>
            )}
            {!emailStore && !phoneStore && (
              <Flex data-testid='reachOutText'>
                <Text bold>{addressStore}</Text>
              </Flex>
            )}
          </StyledContactInfo>
          {enableChatSupportByCountry && (
            <>
              <StyledDescription>
                {t(
                  "Still not getting a response from the restaurant? Let's see if Oddle can help. _chat_",

                  {
                    chat: (
                      <StyledContactLink
                        href='https://eats.oddle.me/help/contact'
                        target='_blank'
                      >
                        {t('Chat with Oddle')}
                      </StyledContactLink>
                    )
                  }
                )}
              </StyledDescription>

              <StyledDescription>
                {t(
                  'Chat hours 10am - 10pm, daily Oddle Pass log in may be required.'
                )}
              </StyledDescription>
            </>
          )}
        </Flex>
      </Container>
    </Modal>
  )
}

export default makeControlledModal(
  ContactRestaurantModal,
  'ContactRestaurantModal'
)
